<!-- Template -->
<template>
  <div>
    <template v-for="feature in mainFeatures" :key="feature.name">
      <FeatureNavigationButton
        v-if="feature.enabled && hasFeatureAccess(feature)"
        :id="`${feature.name}-feature`"
        :isSelected="activeFeature === feature.name"
        :disabled="disabledFeature(feature)"
        :title="getTitle(feature)"
        @onClick="() => onFeatureClick(feature.name)"
      >
        <template #icon><component :is="feature.props?.icon" v-if="feature.props" /></template>
      </FeatureNavigationButton>
    </template>
  </div>
</template>
<!-- Script -->
<script lang="ts" setup>
import { computed, toRefs, watch } from 'vue'
import FeatureNavigationButton from './FeatureNavigationButton.vue'
import { useStore } from 'vuex'
import useFeatures from '@/pinia/features'
import { Feature } from '@/pinia/feature'
import { useI18n } from 'vue-i18n'

const store = useStore()
const features = useFeatures()
const { locale, t } = useI18n()
const { hasFeatureAccess, resetMainFeature, setMainFeature } = features
const { featurePath, mainFeatures } = toRefs(features)
const isDrawPointActive = computed<boolean>(() => store.getters.isDrawPointActive)
const isIndoorviewShowing = computed<boolean>(() => store.getters.isIndoorviewShowing)
const activeFeature = computed<string | null>(() =>
  featurePath.value.length > 1 ? featurePath.value[1] : null
)

function disabledFeature(feature: Feature): boolean {
  const disabledInIndoorview =
    isIndoorviewShowing.value &&
    (feature.name === 'analytics' || feature.name === 'project-analysis')
  return (
    disabledInIndoorview || (feature.props?.disableIsDrawPointActive && isDrawPointActive.value)
  )
}
function getTitle(feature: Feature): string {
  return disabledFeature(feature)
    ? `${feature.title[locale.value]} (${t('general__view__function-disabled').toLowerCase()})`
    : feature.title[locale.value]
}
function onFeatureClick(feature: string) {
  const state = feature === activeFeature.value
  if (state) {
    resetMainFeature()
  } else {
    setMainFeature(feature)
  }
}

watch(isIndoorviewShowing, (isShowing) => {
  if (isShowing) {
    resetMainFeature()
  }
})
</script>
<!-- Styling -->
<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
div {
  position: fixed;
  top: calc(variables.$logo-height + variables.$padding-xlarge);
  left: 16px;
  z-index: variables.$z-index-feature-navigation;
  height: 48px;
  width: 48px;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  filter: drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.16));
}
</style>
